import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { useIntl } from 'react-intl';
import classnames from 'clsx';
import { faQuestionCircle } from '@fortawesome/sharp-regular-svg-icons/faQuestionCircle';

import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';

import { Button } from 'ui-components/components/button';
import { FlexRow, FlexColumn, Gap } from 'ui-components/components/flex';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Tooltip } from 'ui-components/components/tooltip';

import { lookupHarmonizationCode } from './harmonization-code-api';
import { HarmonizationCodeInput } from './harmonization-code-input';

import { useEnableHsCodesFetchingForAll } from '../../../../feature-flags/hooks/use-enable-hs-codes-fetching-for-all';
import { useFulfillmentPlannerContext } from '../../../../fulfillment-planner/context';
import { useDdpCountryConfig } from '../../../../hooks/use-ddp-country-config';
import { useDdpEligibleCarriers } from '../../../../hooks/use-ddp-eligible-carriers';
import { CustomsField } from '../customs-field';

import { messages } from '../messages';
import style from './harmonization-code-input.module.css';
import commonStyle from '../common-style.module.less';

type CustomsHarmonizationCodeProps = {
  onChange: (value: string) => void;
  value?: string;
  description?: string;
  isPrefilled?: boolean;
};

export const CustomsHarmonizationCode = ({
  onChange,
  value = '',
  description = '',
  isPrefilled = false,
}: CustomsHarmonizationCodeProps) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [hsDescription, setHsDescription] = useState('');
  const [isDescriptionAutoFilled, setIsDescriptionAutoFilled] = useState(false);
  const { isDdpAvailable: enablePrepaidDutiesAndTax } = useDdpCountryConfig();
  const enableHsCodesFetchingForAll = useEnableHsCodesFetchingForAll();
  const { isDdpEligibleBySellerLabelProviderId } = useDdpEligibleCarriers();
  const { serviceIdAndSellerLabelProviderId } = useFulfillmentPlannerContext();

  const isDdpEligible =
    enableHsCodesFetchingForAll || isDdpEligibleBySellerLabelProviderId(serviceIdAndSellerLabelProviderId?.sellerLabelProviderId as string);
  const shouldShowHsCodeFetchingAndValidation = enablePrepaidDutiesAndTax && isDdpEligible;

  const handleOnChange = (updatedValue: string) => {
    setIsDescriptionAutoFilled(false);
    setHsDescription('');
    onChange(updatedValue);
  };

  const handleLookup = async () => {
    if (value?.trim()) {
      setErrorMessage(intl.formatMessage(messages.harmonizationCodeAlreadyFilled));
      return;
    }

    if (!description?.trim()) {
      setErrorMessage(intl.formatMessage(messages.harmonizationDescriptionRequired));
      return;
    }

    setErrorMessage('');
    setIsLoading(true);
    try {
      const response = await lookupHarmonizationCode(description);
      if (response?.errorCode) {
        const errorKey = response.errorCode as keyof typeof messages;
        setErrorMessage(intl.formatMessage(messages[errorKey]));
        setIsDescriptionAutoFilled(false);
      } else {
        onChange(response?.harmonizationCode ?? '');
        const compoundDescription = response?.description
          ? intl.formatMessage(messages.harmonizationDescriptionLabel, { description: response.description })
          : '';
        setHsDescription(compoundDescription);
        setIsDescriptionAutoFilled(true);
      }
    } catch {
      setErrorMessage(intl.formatMessage(messages.hsCodeGeneralError));
      setIsDescriptionAutoFilled(false);
    } finally {
      setIsLoading(false);
    }
  };

  const tooltipMessage = shouldShowHsCodeFetchingAndValidation ? messages.harmonizationTooltip : messages.harmonizationHelperTooltip;
  const labelTooltip = (
    <Tooltip text={intl.formatMessage(tooltipMessage)}>
      <FontAwesomeIcon icon={faQuestionCircle} />
    </Tooltip>
  );

  if (!shouldShowHsCodeFetchingAndValidation) {
    return (
      <CustomsField
        label={messages.harmonizationLabel}
        labelClassname={commonStyle['label-with-tooltip']}
        recommended
        tooltip={labelTooltip}
      >
        <HarmonizationCodeInput
          aria-label={intl.formatMessage(messages.harmonizationLabel)}
          className={classnames({ [style['hs-description-input-prefilled']]: isDescriptionAutoFilled })}
          disabled={isPrefilled}
          disabledReason={intl.formatMessage(messages.harmonizationCodePrefilledTooltip)}
          onChange={handleOnChange}
          value={value}
        />
      </CustomsField>
    );
  }

  return (
    <CustomsField label={messages.harmonizationLabel} labelClassname={commonStyle['label-with-tooltip']} recommended tooltip={labelTooltip}>
      <FlexColumn gap={Gap.Space8}>
        <FlexRow gap={Gap.Space8}>
          <FlexColumn className={style['input-column']}>
            <HarmonizationCodeInput
              aria-label={intl.formatMessage(messages.harmonizationLabel)}
              className={classnames({ [style['hs-description-input-prefilled']]: isDescriptionAutoFilled })}
              disabled={isPrefilled}
              disabledReason={intl.formatMessage(messages.harmonizationCodePrefilledTooltip)}
              onChange={handleOnChange}
              value={value}
            />
          </FlexColumn>
          <FlexColumn className={style['button-column']}>
            <Button
              ariaLabel={intl.formatMessage(messages.generateHarmonizationCode)}
              className={style['refresh-button']}
              disabled={isPrefilled}
              onClick={handleLookup}
              theme='tertiary'
            >
              {isLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  <FontAwesomeIcon className={style['ia-icon']} icon={faSparkles} />
                  <Text textType={TextType.Label} value={intl.formatMessage(messages.findHSCodeButton)} />
                </>
              )}
            </Button>
          </FlexColumn>
        </FlexRow>
        {hsDescription && <Text className={style['hs-description-text']} textType={TextType.Caption} value={hsDescription} />}
      </FlexColumn>
      {errorMessage && <InlineMessage type={INLINE_MESSAGE_TYPES.error}>{errorMessage}</InlineMessage>}
    </CustomsField>
  );
};
